import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import SyncIcon from '@mui/icons-material/Sync';
import { Tooltip,Alert,Collapse } from "@mui/material";
import Typography from '@mui/material/Typography';
import {
    DataGrid,
} from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Printer from '../../pages/General/Printer'
import ShipmentAccordion from '../../pages/General/ShipmentAccordion'
import NewOrderCreateDrawer from '../General/NewOrder/NewOrderCreateDrawer'
import Notification from '../../pages/General/Notification';
import NorowsData from '../../pages/General/NorowsData'
import OrderDetailsEditDrawer from '../../pages/General/OrderDetailsEditDrawer'
import { resetOrderDetail } from '../../pages/OrderDetail/orderdetailsSlice';
import CarrierTracking from './CarrierTracking'
import { doGetRate, resetRateState } from '../../pages/Shipments/NewShipment/rateSlice';
import Date from '../General/Date'
import { resetGenerateLabel } from '../../pages/Shipments/NewShipment/createShipmentSlice';
import { fetchCountry, fetchState, fetchMultiStates } from '../../pages/General/countryStateCitySlice';
import { resetOrderUpdateNotifications, resetOrderNoteNotifications } from '../../pages/Orders/ordersSlice';
import OrderNote from './OrderNote'
import WidthMaximize from './WidthMaximize'
// Utility for label styling to keep the code clean
const labelStyle = {
  fontWeight: 600,
  fontSize: '0.85rem',
  color: '#64748B', // Slate label color
  minWidth: '100px',
  display: 'inline-block'
};

const valueStyle = {
  fontSize: '0.9rem',
  color: '#1E293B', // Dark value color
  fontWeight: 500
};

const InfoRow = ({ label, value, showDivider = true }) => (
  <>
    <Box sx={{ py: 1.2, display: 'flex', alignItems: 'flex-start' }}>
      <Typography sx={labelStyle}>{label}</Typography>
      <Typography sx={valueStyle}>{value}</Typography>
    </Box>
    {showDivider && <Divider sx={{ opacity: 0.6 }} />}
  </>
);
const OrderDrawer = ({ visible, reportName, formDataValues, onClose, orderRow = null, meta = null, orderId = null, invalidDocIds = null }) => {
    const dispatch = useDispatch();
    const [drawer, setDrawer] = useState(true);
    const orderDetailData = useSelector(state => state.orderdetails.data);
    const orderNotesData = useSelector(state => state.orders && state.orders.orderNote);
    const orderDetails = useSelector(state => state.orderdetails);
    const orderUpdate = useSelector(state => state.orders && state.orders.orderUpdate);
    const orders = useSelector(state => state.orders);
    const profile = useSelector(state => state.profile.profile);
    const [newOrderCreateDrawerVisible, setNewOrderCreateDrawerVisible] = useState(false);
    const [newOrderFormData, setNewOrderFormData] = useState("");
    const [isDuplicateOrder, setIsDuplicateOrder] = useState(false);
    const [isShipmentCreationFlow, setIsShipmentCreationFlow] = useState(false);
    const [choosenTrackingNumber, setChoosenTrackingNumber] = useState(0);
    const [printUrl, setPrintUrl] = useState("")
    const [isPrinted, setIsPrinted] = useState(true)
    const [isReturnOrder, setIsReturnOrder] = useState(false);
    const [orderdetailsEditDrawerVisible, setOrderdetailsEditDrawerVisible] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState("80%");

    // 1. Initialize custom sync notification alert and loader triggers
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncAlert, setSyncAlert] = useState({ show: false, type: 'info', message: '' });

    // 2. Build the handler to refresh the Shopify payload data
    const handleShopifyOrderRefresh = async () => {
        setSyncLoading(true);
        setSyncAlert({ show: false, type: 'info', message: '' });

        try {
            const baseUrl = process.env.REACT_APP_INTEGRATIONS_URL || 'http://localhost:7000';
            const userId = profile?.profile?.id || profile?.id; // Extract matching system user context mapping
            
            const payload = {
                userid: userId,
                shop: orderDetailData?.order_details?.shop,
                shopify_orderid: String(orderDetailData?.order_details?.shopify_orderid)
            };

            const response = await axios.post(`${baseUrl}/shopify/order-refresh`, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            // Assuming your backend responds with a standard status layout message mapping
            if (response.status === 200 || response.data?.status === 'success') {
                setSyncAlert({
                    show: true,
                    type: 'success',
                    message: response.data?.message || 'Order refreshed successfully from Shopify!'
                });
                
                // OPTIONAL: Trigger a fresh reload of your core order details dataset to reflect new adjustments
                // dispatch(fetchOrderDetails(orderDetailData.order_details.orderid));
            } else {
                setSyncAlert({
                    show: true,
                    type: 'error',
                    message: response.data?.message || 'Failed to sync with Shopify store parameters.'
                });
            }
        } catch (error) {
            console.error("Shopify Sync Failure Exception Tracker:", error);
            setSyncAlert({
                show: true,
                type: 'error',
                message: error.response?.data?.message || 'Network exception occurred while syncing order data.'
            });
        } finally {
            setSyncLoading(false);
        }
    };

    // 3. Simple flag to determine platform match status criteria
    const isShopifyPlatform = String(orderDetailData?.platform).toLowerCase() === 'shopify';
    const initialValues = {
        customer_firstname: '',
        customer_lastname:'',
        customer_email: '',
        customer_phone: '',
        receiver_first_name:'',
        receiver_last_name:'',
        address1: '',
        address2: '',
        city: '',
        province: '',
        country: '',
        zip: '',
    }
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});


    const dispatchInputChangeCallback = (name, value) => {
        setValues({
            ...values,
            [name]: value
        })
    }
    const dispatchCountryCallback = (id) => {
            setValues({
                ...values,
                ['country']: id
            })
            dispatch(fetchState(id));
    }
    const dispatchProvinceCallback = (id) => {
            setValues({
                ...values,
                ['province']: id
            })
    }
    
    useEffect(() => {
        if (orderDetails !== '') {
            let customer_firstname = "";
            let customer_lastname = "";
            let customer_email = "";
            let customer_phone = "";
            let address1 = "";
            let address2 = "";
            let province = "";
            let city="";
            let zip = "";
            let country = "";
            let receiver_first_name = "";
            let receiver_last_name = "";
            customer_firstname=orderDetailData && orderDetailData.customer_details && orderDetailData.customer_details.first_name && orderDetailData.customer_details.first_name!=='' ? orderDetailData.customer_details.first_name :""
            customer_lastname=orderDetailData && orderDetailData.customer_details && orderDetailData.customer_details.last_name && orderDetailData.customer_details.last_name!=='' ? orderDetailData.customer_details.last_name :"" 
            customer_email=orderDetailData && orderDetailData.customer_details && orderDetailData.customer_details.email && orderDetailData.customer_details.email!=='' ? orderDetailData.customer_details.email :"" 
            customer_phone=orderDetailData && orderDetailData.customer_details && orderDetailData.customer_details.phone && orderDetailData.customer_details.phone!=='' ? orderDetailData.customer_details.phone :"" 
            receiver_first_name=orderDetailData.shipping_address && orderDetailData.shipping_address.first_name ? orderDetailData.shipping_address.first_name: ""
            receiver_last_name=orderDetailData.shipping_address && orderDetailData.shipping_address.last_name ? orderDetailData.shipping_address.last_name : ""
            address1=orderDetailData.shipping_address && orderDetailData.shipping_address.address1 ? orderDetailData.shipping_address.address1: ""
            address2=orderDetailData.shipping_address && orderDetailData.shipping_address.address2 ? orderDetailData.shipping_address.address2: ""
            city=orderDetailData.shipping_address && orderDetailData.shipping_address.city ? orderDetailData.shipping_address.city : ""
            province=(orderDetailData.shipping_address && orderDetailData.shipping_address.province) ?  orderDetailData.shipping_address.province : ""
            country=orderDetailData.shipping_address && orderDetailData.shipping_address.country ? orderDetailData.shipping_address.country : ""
            zip=orderDetailData.shipping_address && orderDetailData.shipping_address.zip ? orderDetailData.shipping_address.zip : ""

            setValues({
                ...values,
                ['customer_firstname']: customer_firstname,
                ['customer_lastname']: customer_lastname,
                ['customer_email']: customer_email,
                ['customer_phone']: customer_phone,
                ['receiver_first_name']: receiver_first_name,
                ['receiver_last_name']: receiver_last_name,
                ['address1']: address1,
                ['address2']: address2,
                ['province']: province,
                ['zip']: zip,
                ['city']: city,
                ['country']: country,
            })
        }
    },[orderDetails])
    useEffect(() => {
        if ((orders && orders.createShipment && orders.createShipment.message && orders.createShipment.message !== "")|| (invalidDocIds!==null && Object.keys(invalidDocIds).length>0 && orderUpdate && orderUpdate.notification && orderUpdate.notification.messageContent && orderUpdate.notification.messageContent[0] === 'Order updated successfully')) {
            setDrawer(false)
            onClose(false)
        }
    }, [orders.createShipment.message || orderUpdate])

    const ondrawerclose = () => {
        setDrawer(false)
        onClose(false)
        dispatch(resetOrderUpdateNotifications())
        dispatch(resetRateState())
        dispatch(resetGenerateLabel())
        dispatch(resetOrderDetail());
    }
    const orderDetailsEditCallback = () => {
        setOrderdetailsEditDrawerVisible(true);
    }
    const orderDetailsEditOnClose = () => {
        setOrderdetailsEditDrawerVisible(false);
    }

    const createShipmentButton = (item) => {
        let createShipmentButtonDisable = true;
        if(item && (typeof item.line_items =='undefined' || item.line_items.length==0)&& (profile && profile.disable_create_shipment ===false || typeof profile.disable_create_shipment == "undefined")){
            createShipmentButtonDisable = false;
        }else if(item && item.line_items && item.line_items.length !== 0 && (profile && profile.disable_create_shipment ===false || typeof profile.disable_create_shipment == "undefined")) {
            item.line_items.forEach(function (key) {
                if (!key.tracking_number || key.tracking_number === "") {
                    createShipmentButtonDisable = false;
                }
            });
        }
        return createShipmentButtonDisable;
    }

    const newOrderCreateCallback = (item) => {
        setIsShipmentCreationFlow(true)
        setNewOrderFormData(item);
        setNewOrderCreateDrawerVisible(true)
    }

    const newOrderCreateOnClose = () => {
        setNewOrderCreateDrawerVisible(false);
    }

    const productinfocolumns = [
        {
            field: 'length',
            headerName: 'Length',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                if (params.row.dimensions && params.row.dimensions.length && typeof params.row.dimensions.length !== "undefined") {
                    return params.row.dimensions.length
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'width',
            headerName: 'Width',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                if (params.row.dimensions && params.row.dimensions.width && typeof params.row.dimensions.width !== "undefined") {
                    return params.row.dimensions.width
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'height',
            headerName: 'Height',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                if (params.row.dimensions && params.row.dimensions.height && typeof params.row.dimensions.height !== "undefined") {
                    return params.row.dimensions.height
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'actual_weight',
            headerName: 'Weight',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                if (params.row.actual_weight && typeof params.row.actual_weight !== "undefined") {
                    return params.row.actual_weight
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'length_unit',
            headerName: 'Dimension Unit',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                if (params.row.dimensions && params.row.dimensions.length_unit && typeof params.row.dimensions.length_unit !== "undefined") {
                    return params.row.dimensions.length_unit
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'weight_unit',
            headerName: 'Weight Unit',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                if (params.row.weight_unit && typeof params.row.weight_unit !== "undefined") {
                    return params.row.weight_unit
                } else {
                    return "-"
                }
            }
        },
    ]

    const columns = [
        
        {
            field: 'sku',
            headerName: 'SKU',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                const sku = params.row.inventory_sku ?? params.row.sku;
                return sku && sku !== "" ? sku : "-";
            }
        },
        {
            field: 'product_desc',
            headerName: 'Product Desc',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                const desc = params.row.inventory_product_desc ?? params.row.product_desc;
                return desc && desc !== "" ? desc : "-";
            }
        },
        {
            field: 'tracking_number',
            headerName: 'Tracking Number',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                let tracking_number = (params.row.tracking_number && typeof params.row.tracking_number !== "undefined" && params.row.tracking_number !== null)
                    ? params.row.tracking_number : "";
                let carrier_name = (params.row.fulfilment_company && typeof params.row.fulfilment_company !== "undefined" && params.row.fulfilment_company !== null)
                    ? params.row.fulfilment_company : "";
                let tracking = { "tracking_number": tracking_number, "carrier_name": carrier_name }
                return (
                    <CarrierTracking trackingItems={tracking} carrierImageWidth="60px" />
                )
            }
        },
        // Conditionally add "Fulfilled By" column right after Tracking Number
        ...(profile && profile.show_store_name_in_details === true ? [
            {
                field: 'fulfilled_by_store_name',
                headerName: 'Fulfilled By',
                sortable: false,
                flex: 1,
                resizable: false,
                renderCell: (params) => {
                    const storeName = params.row.fulfilled_by_store_name;
                    return storeName && storeName !== "" ? storeName : "-";
                }
            }
        ] : []),
        {
            field: 'quantity',
            headerName: 'Quantity',
            sortable: false,
            flex: 1,
            resizable: false,
            renderCell: (params) => {
                if (params.row.quantity && typeof params.row.quantity !== "undefined") {
                    return params.row.quantity
                } else {
                    return "-"
                }
            }
        },
        {
            field: 'product_price',
            headerName: 'Price',
            headerClassName: 'super-app-theme--header',
            width: 100,
            renderCell: (params) => {
                // Priority: inventory_product_price -> product_price
                const price = params.row.inventory_product_price ?? params.row.product_price;
                return price && price !== "" ? price : "-";
            }
        },
    ]

    return (
        <>
            <Printer isPrinted={isPrinted} setIsPrinted={setIsPrinted} print_url={printUrl} />
            <Drawer
                anchor="right"
                open={visible}
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        ondrawerclose();
                    }
                }}
                PaperProps={{
                    sx: { width: drawerWidth },
                }}
            >

                {(orderUpdate && orderUpdate.notification && orderUpdate.notification.show) &&
                    <Notification
                        is_open={orderUpdate.notification.show}
                        messageType={orderUpdate.notification.messageType}
                        messages={orderUpdate.notification.messageContent}
                        handleClose={() => dispatch(resetOrderUpdateNotifications())}
                    />
                }

                {(orderNotesData && orderNotesData.notification && orderNotesData.notification.show && orderNotesData.notification.show===true) &&
                            <Notification
                                is_open={orderNotesData.notification.show}
                                messageType={orderNotesData.notification.messageType}
                                messages={orderNotesData.notification.messageContent}
                                handleClose={() => dispatch(resetOrderNoteNotifications())}
                            />
                }

                {orderDetails && orderDetails.loading === true && <>
                    <Box style={{
                        position: 'absolute', left: '50%', top: '50%',
                        transform: 'translate(-50%, -50%)'
                    }}>
                        <CircularProgress />
                    </Box>
                </>
                }
                {orderDetails && orderDetails.loading === false &&
                    <div style={{ padding: "0 30px" }}>
                        <Card 
                            className="sc--cardhead--details"
                            sx={{ 
                                width: '100%', 
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E2E8F0',
                                borderRadius: '16px',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                                overflow: 'hidden',
                                mb: 3,
                                mt:3,
                            }}
                        >
                            <CardHeader 
                                title={
                                    <Typography variant="h6" component="h2" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                                        Order Details
                                    </Typography>
                                } 
                                action={
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '38px' }}>
                                        
                                        {/* 1. Dynamic Order Description System Notes Widget Overlay Anchor */}
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <OrderNote 
                                                orderid={(orderRow && orderRow.order && orderRow.order.id) ? orderRow.order.id : ""} 
                                                docid={(orderRow && orderRow._id) ? orderRow._id : ""} 
                                                notesData={(orderRow && orderRow.order && orderRow.order.note) ? orderRow.order.note : ""} 
                                                meta={meta}
                                            />
                                        </Box>

                                        {/* 2. Interactive Data Attribute Mutation Modifier Trigger Action */}
                                        <Tooltip title="Edit order details" placement="top">
                                            <Box>
                                                <Button
                                                    variant="outlined"
                                                    disabled={createShipmentButton(orderRow)}
                                                    onClick={orderDetailsEditCallback}
                                                    sx={{
                                                        textTransform: 'none',
                                                        borderRadius: '10px',
                                                        fontWeight: 700,
                                                        fontSize: '13px',
                                                        height: '38px',
                                                        px: 2.5,
                                                        borderColor: '#E2E8F0',
                                                        color: '#475569',
                                                        transition: 'all 0.15s ease-in-out',
                                                        '&:hover': {
                                                            borderColor: '#1976d2',
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                            color: '#1976d2'
                                                        },
                                                        '&.Mui-disabled': {
                                                            borderColor: '#E2E8F0',
                                                            color: '#CBD5E1',
                                                            cursor: 'not-allowed',
                                                            pointerEvents: 'auto' // Preserves tooltips during disabled state frames
                                                        }
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </Box>
                                        </Tooltip>

                                        {/* 3. Parcel Generation Label Submission Pipeline Trigger Action */}
                                        <Tooltip title="Create Shipment" placement="top">
                                            <Box>
                                                <Button
                                                    variant="outlined"
                                                    disabled={createShipmentButton(orderRow)}
                                                    onClick={() => newOrderCreateCallback(orderRow)}
                                                    startIcon={<AddCircleOutlineRoundedIcon sx={{ fontSize: '18px !important' }} />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        borderRadius: '10px',
                                                        fontWeight: 700,
                                                        fontSize: '13px',
                                                        height: '38px',
                                                        px: 2.5,
                                                        borderColor: '#1976d2',
                                                        color: '#1976d2',
                                                        transition: 'all 0.15s ease-in-out',
                                                        '&:hover': {
                                                            borderColor: '#115293',
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                            color: '#115293'
                                                        },
                                                        '&.Mui-disabled': {
                                                            borderColor: '#E2E8F0',
                                                            backgroundColor: '#FFFFFF',
                                                            color: '#CBD5E1',
                                                            cursor: 'not-allowed',
                                                            pointerEvents: 'auto'
                                                        }
                                                    }}
                                                >
                                                    Label
                                                </Button>
                                            </Box>
                                        </Tooltip>

                                        {/* 4. Layout Canvas Frame Sizing Modulation Utilities */}
                                        <WidthMaximize
                                            defaultWidth="80%"
                                            onToggle={(newWidth) => setDrawerWidth(newWidth)}
                                        />
                                        
                                        {/* 5. Draw Window Intercept Dismissal Operation */}
                                        <IconButton 
                                            aria-label="settings" 
                                            onClick={ondrawerclose}
                                            sx={{ 
                                                color: '#64748B',
                                                padding: '8px',
                                                borderRadius: '10px',
                                                border: '1px solid #E2E8F0',
                                                backgroundColor: '#FFFFFF',
                                                transition: 'all 0.15s ease-in-out',
                                                '&:hover': { backgroundColor: '#F1F5F9', borderColor: '#CBD5E1', color: '#1E293B' }
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: '18px' }} />
                                        </IconButton>

                                    </Stack>
                                }
                                sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                            />
                            
                            {/* Asynchronous Network Activity Query Validation Overlay */}
                            {(orderNotesData && orderNotesData.loading === true) && (
                                <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
                            )}
                            
                            <Divider sx={{ borderColor: '#E2E8F0' }} />
                        </Card>
                       <Box sx={{ width: '100%' }}>
                        {/* ─── DYNAMIC INTEGRATION SYNC ALERT DISPLAY CONTAINER ─── */}
                        <Collapse in={syncAlert.show}>
                            <Alert 
                                severity={syncAlert.type} 
                                onClose={() => setSyncAlert(prev => ({ ...prev, show: false }))}
                                sx={{ mb: 2, borderRadius: '12px', fontWeight: 600 }}
                            >
                                {syncAlert.message}
                            </Alert>
                        </Collapse>

                        <Card 
                            sx={{ 
                                borderRadius: '16px', 
                                border: '1px solid #E2E8F0', 
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' 
                            }}
                        >
                            <CardHeader 
                                title={
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1.1rem' }}>
                                        {orderDetailData && orderDetailData.order_details && orderDetailData.order_details.orderid 
                                        ? `Order ID# : ${orderDetailData.order_details.orderid}` 
                                        : 'Order ID# : - '}
                                    </Typography>
                                } 
                                /* ─── ADDED ACTION CONTROL ON THE TOP RIGHT SIDE CORNER ─── */
                                action={
                                    isShopifyPlatform && (
                                        <Tooltip title={syncLoading ? "Refreshing Order Data..." : "Sync Order with Shopify"} placement="top">
                                            <IconButton
                                                onClick={handleShopifyOrderRefresh}
                                                disabled={syncLoading}
                                                sx={{
                                                    color: '#1976d2',
                                                    backgroundColor: '#FFFFFF',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '8px',
                                                    p: 1,
                                                    mr: 1,
                                                    mt: 0.5,
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                        borderColor: '#CBD5E1'
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: '#F1F5F9',
                                                        borderColor: '#E2E8F0'
                                                    }
                                                }}
                                            >
                                                {syncLoading ? (
                                                    <CircularProgress size={18} sx={{ color: '#1976d2' }} />
                                                ) : (
                                                    <SyncIcon sx={{ fontSize: '18px' }} />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    )
                                }
                                sx={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', py: 1.5 }}
                            />
                            
                            <CardContent sx={{ p: 2 }}>
                                <Grid container spacing={3}>
                                
                                {/* 1. Customer Information Module */}
                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" sx={{ borderRadius: '12px', height: '245px', overflow: 'auto' }}>
                                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#F1F5F9' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Customer Information
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <CardContent sx={{ p: '8px 16px !important' }}>
                                        <InfoRow 
                                        label="Name:" 
                                        value={`${orderDetailData?.customer_details?.first_name || ''} ${orderDetailData?.customer_details?.last_name || ''}`} 
                                        />
                                        <InfoRow 
                                        label="Email:" 
                                        value={orderDetailData?.customer_details?.email} 
                                        />
                                        <Box sx={{ py: 1.2, display: 'flex', alignItems: 'flex-start' }}>
                                        <Typography sx={labelStyle}>Phone:</Typography>
                                        <Typography sx={valueStyle}>
                                            {orderDetailData && orderDetailData.shipment_details && orderDetailData.shipment_details.length !== 0 ?
                                            orderDetailData.shipment_details[0]?.customer?.phone :
                                            orderDetailData?.customer_details?.phone
                                            }
                                        </Typography>
                                        </Box>
                                    </CardContent>
                                    </Card>
                                </Grid>

                                {/* 2. Delivery Address Module */}
                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" sx={{ borderRadius: '12px', height: '245px', overflow: 'auto' }}>
                                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#F1F5F9' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Delivery Address
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <CardContent sx={{ p: '8px 16px !important' }}>
                                        <InfoRow 
                                        label="Name:" 
                                        value={orderDetailData.shipping_address?.name} 
                                        />
                                        <InfoRow 
                                        label="Address:" 
                                        value={`${orderDetailData.shipping_address?.address1 || ''} ${orderDetailData.shipping_address?.address2 || ''} ${orderDetailData.shipping_address?.city || ''}`} 
                                        />
                                        <Box sx={{ py: 1.2, display: 'flex', alignItems: 'flex-start' }}>
                                        <Typography sx={labelStyle}>Prov / Zip:</Typography>
                                        <Typography sx={valueStyle}>
                                            {`${orderDetailData.shipping_address?.province || ''} ${orderDetailData.shipping_address?.country || ''} ${orderDetailData.shipping_address?.zip || ''}`}
                                        </Typography>
                                        </Box>
                                    </CardContent>
                                    </Card>
                                </Grid>

                                {/* 3. Order Summary Module */}
                                <Grid item xs={12} md={4}>
                                    <Card variant="outlined" sx={{ borderRadius: '12px', height: '245px', overflow: 'auto' }}>
                                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#F1F5F9' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Order Summary
                                        </Typography>
                                    </Box>
                                    <Divider />
                                    <CardContent sx={{ p: '8px 16px !important' }}>
                                        <InfoRow 
                                        label="Ordered On:" 
                                        value={<Date date={orderDetailData.order_details?.order_date} />} 
                                        />
                                        <InfoRow 
                                        label="Platform:" 
                                        value={orderDetailData.platform} 
                                        />
                                        <InfoRow 
                                        label="Price:" 
                                        value={orderDetailData.order_details?.total} 
                                        />
                                        <InfoRow 
                                        label="Quantity:" 
                                        value={orderDetailData.total_quantity} 
                                        />
                                        <Box sx={{ py: 1.2, display: 'flex', alignItems: 'flex-start' }}>
                                        <Typography sx={labelStyle}>Items:</Typography>
                                        <Typography sx={valueStyle}>
                                            {orderDetailData.product_details && Object.keys(orderDetailData.product_details).length}
                                        </Typography>
                                        </Box>
                                    </CardContent>
                                    </Card>
                                </Grid>

                                </Grid>
                            </CardContent>
                        </Card>
                    </Box>
                        {orderDetailData && orderDetailData.shipment_details && orderDetailData.shipment_details.length != 0 &&

                            <Card style={{ marginTop: '15px' }}>
                                <CardHeader title="Shipment Details" className="sc--cardhead--details" />
                                <Divider />
                                <CardContent>
                                    <ShipmentAccordion shipment={orderDetailData} setPrintUrl={setPrintUrl} setIsPrinted={setIsPrinted} />
                                </CardContent>
                            </Card>
                        }
                        {orderDetailData && orderDetailData.product_info && orderDetailData.product_info.length != 0 &&
                            < Card style={{ marginTop: '15px' }}>
                                <CardHeader title="Product Info" className="sc--cardhead--details" />
                                <Divider />
                                <CardContent className="sc--product--table">
                                    <DataGrid
                                        getRowId={(row) => row.line_item_id + Math.random()}
                                        rows={orderDetailData && orderDetailData.product_info}
                                        columns={productinfocolumns}
                                        autoHeight={true}
                                        hideFooter={true}
                                        disableRowSelectionOnClick
                                        slots={{
                                            NorowsData,
                                            loadingOverlay: LinearProgress,
                                        }}
                                        loading={
                                            (orderDetails && orderDetails.loading)
                                        }
                                    />
                                </CardContent>
                            </Card>
                        }
                        {orderDetailData && orderDetailData.product_details && orderDetailData.product_details.length !== 0 && (
                            <Card 
                                elevation={0}
                                style={{ 
                                    marginTop: '20px', 
                                    borderRadius: '12px', 
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
                                }}
                            >
                                <CardHeader 
                                    title={
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827' }}>
                                            Product Details ({orderDetailData.product_details.length})
                                        </Typography>
                                    } 
                                    sx={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', py: 1.5, px: 3 }}
                                />
                                <Divider />
                                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                    <DataGrid
                                        // Safe unique ID string generation strategy to prevent collision errors
                                        getRowId={(row) => row.line_item_id || `${row.product_id}-${Math.random()}`}
                                        rows={orderDetailData.product_details}
                                        columns={columns}
                                        autoHeight={true}
                                        disableRowSelectionOnClick
                                        density="comfortable"
                                        rowHeight={52}
                                        columnHeaderHeight={46}
                                        loading={orderDetails && orderDetails.loading}
                                        
                                        // --- ENTERPRISE AUTOMATED PAGINATION PROPS ---
                                        initialState={{
                                            pagination: {
                                                paginationModel: { pageSize: 5, page: 0 },
                                            },
                                        }}
                                        pageSizeOptions={[5, 10, 25, 50]}
                                        hideFooter={orderDetailData.product_details.length === 0} 

                                        slots={{
                                            NorowsData,
                                            loadingOverlay: LinearProgress,
                                        }}
                                        
                                        // Custom CSS Injection for a premium, cardless reporting view
                                        sx={{
                                            border: 'none',
                                            borderRadius: 0,
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f9fafb',
                                                borderBottom: '1px solid #e5e7eb',
                                                color: '#374151',
                                            },
                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            },
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: '1px solid #f3f4f6',
                                                color: '#1f2937',
                                                fontSize: '0.875rem',
                                                display: 'flex',
                                                alignItems: 'center'
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: '#f8fafc',
                                            },
                                            '& .MuiDataGrid-footerContainer': {
                                                borderTop: '1px solid #e5e7eb',
                                                backgroundColor: '#f9fafb',
                                                px: 2
                                            },
                                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                color: '#4b5563',
                                            }
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        )}
                        {
                            !orderDetailData.order_details &&
                            <h4 style={{ textAlign: 'center' }}>Order details not found</h4>
                        }
                    </div>
                }
                <NewOrderCreateDrawer
                    visible={newOrderCreateDrawerVisible}
                    reportName={"ORDER_DRAWER"}
                    newOrderFormData={newOrderFormData}
                    setNewOrderFormData={setNewOrderFormData}
                    meta={meta}
                    onClose={newOrderCreateOnClose}
                    isDuplicateOrder={isDuplicateOrder}
                    setNewOrderCreateDrawerVisible={setNewOrderCreateDrawerVisible}
                    choosenTrackingNumber={choosenTrackingNumber}
                    isShipmentCreationFlow={isShipmentCreationFlow}
                    isReturnOrder={isReturnOrder}
                    setIsReturnOrder={setIsReturnOrder}
                />
                {orderdetailsEditDrawerVisible &&
                    <OrderDetailsEditDrawer
                        visible={orderdetailsEditDrawerVisible}
                        onClose={orderDetailsEditOnClose}
                        values={values}
                        dispatchInputChangeCallback={dispatchInputChangeCallback}
                        errors={errors}
                        lineItemsData={orderDetailData && orderDetailData.product_details}
                        dispatchCountryCallback={dispatchCountryCallback}
                        dispatchProvinceCallback={dispatchProvinceCallback}
                        invalidDocIds={invalidDocIds}
                        meta={meta}
                    />
                }
            </Drawer >
        </>
    )
}


export default OrderDrawer;